<template>
  <div class="user-page">
    <section class="user-shell">
      <div class="user-hero">
        <div class="hero-copy">
          <nav class="user-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">User Management</span>
          </nav>

          <p class="hero-kicker">Admin Directory</p>
          <h1>Admin directory</h1>
          <p class="hero-description">
            Review administrator accounts, roles, and access details in one place.
          </p>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Admin accounts</span>
            <strong class="metric-value">{{ admins.length }}</strong>
            <span class="metric-meta">Records currently available</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Administration</p>
          <h2>
            Administrators
            <span class="directory-count">{{ admins.length }}</span>
          </h2>
        </div>

        <button
          v-if="isHeadAdminViewer"
          type="button"
          class="add-admin-btn"
          :disabled="saving || editingLoading || deleting || statusModalOpen"
          @click="openAdminModal()"
        >
          <i class="fas fa-plus" aria-hidden="true"></i>
          <span>Add New Admin</span>
        </button>
      </section>

      <p v-if="notice" class="admin-notice" role="status">{{ notice }}</p>
      <div v-if="loadError" class="admin-error" role="alert">
        {{ loadError }}
        <button type="button" class="edit-btn" :disabled="loading" @click="loadAdmins">
          Try again
        </button>
      </div>
      <div v-if="loading" class="loading-state">
        <b-spinner />
        <p>Loading admin directory...</p>
      </div>

      <template v-else>
        <div v-if="admins.length" class="directory-panel">
          <div
            class="directory-scroll"
            role="region"
            aria-label="Administrator directory"
            tabindex="0"
          >
            <table class="directory-table">
              <caption class="sr-only">
                Administrator accounts, organizations, positions, authorizations, and edit actions
              </caption>
              <thead>
                <tr>
                  <th scope="col" class="number-column">No.</th>
                  <th scope="col" class="account-column">Administrator</th>
                  <th scope="col">Organization</th>
                  <th scope="col">Position</th>
                  <th scope="col">Authorization</th>
                  <th scope="col">Status</th>
                  <th scope="col" class="action-column">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in admins" :key="item.id || index">
                  <td class="row-number">{{ padIndex(index) }}</td>
                  <th scope="row">
                    <div class="account-identity">
                      <span class="account-avatar" aria-hidden="true">{{
                        getAdminUsername(item).slice(0, 2).toUpperCase()
                      }}</span>
                      <div class="account-copy">
                        <span class="account-name">{{ getAdminUsername(item) }}</span>
                        <span class="account-email">{{ getAdminEmail(item) }}</span>
                      </div>
                    </div>
                  </th>
                  <td>
                    <span class="organization-name">{{ item.organization || 'Not provided' }}</span>
                  </td>
                  <td>
                    <span class="position-label">{{ item.position || 'Not provided' }}</span>
                  </td>
                  <td>
                    <span class="authorization-badge">{{ getAuthorizationLabel(item) }}</span>
                  </td>
                  <td>
                    <span
                      class="account-status"
                      :class="{
                        'account-status--disabled': !isAdminEnabled(item),
                      }"
                    >
                      {{ isAdminEnabled(item) ? 'Enabled' : 'Disabled' }}
                    </span>
                  </td>
                  <td class="action-column">
                    <div
                      class="admin-row-actions"
                      role="group"
                      :aria-label="`Actions for ${getAdminUsername(item)}`"
                    >
                      <button
                        type="button"
                        class="edit-btn"
                        :class="{ 'edit-btn--locked': !canEditAdmin(item) }"
                        :disabled="
                          !canEditAdmin(item) ||
                          saving ||
                          editingLoading ||
                          deleting ||
                          statusModalOpen
                        "
                        :aria-label="`Edit admin ${getAdminUsername(item)}`"
                        :title="
                          canEditAdmin(item)
                            ? 'Edit administrator'
                            : 'Only a Head Admin can edit this administrator'
                        "
                        @click="setAdminEdit(item)"
                      >
                        <i
                          :class="canEditAdmin(item) ? 'fas fa-pen' : 'fas fa-lock'"
                          aria-hidden="true"
                        ></i>
                        <span>{{ canEditAdmin(item) ? 'Edit' : 'Head Admin only' }}</span>
                      </button>
                      <button
                        v-if="isHeadAdminViewer"
                        type="button"
                        class="edit-btn admin-status-btn"
                        :class="{ 'admin-status-btn--disable': isAdminEnabled(item) }"
                        :disabled="
                          saving ||
                          editingLoading ||
                          deleting ||
                          adminModalOpen ||
                          deleteModalOpen ||
                          statusModalOpen
                        "
                        :aria-label="`${
                          isAdminEnabled(item) ? 'Disable' : 'Enable'
                        } admin ${getAdminUsername(item)}`"
                        @click="toggleAdminStatus(item)"
                      >
                        <b-spinner v-if="statusUpdatingId === item.id" small />
                        <i v-else class="fas fa-power-off" aria-hidden="true"></i>
                        <span>{{ isAdminEnabled(item) ? 'Disable' : 'Enable' }}</span>
                      </button>
                      <button
                        v-if="isHeadAdminViewer"
                        type="button"
                        class="edit-btn admin-delete-btn"
                        title="Delete administrator"
                        :disabled="saving || editingLoading || deleting || statusModalOpen"
                        :aria-label="`Delete admin ${getAdminUsername(item)}`"
                        @click="openDeleteAdmin(item)"
                      >
                        <i class="far fa-trash-alt" aria-hidden="true"></i> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="directory-footer">
            <span
              >Showing <strong>{{ admins.length }}</strong> administrator{{
                admins.length === 1 ? '' : 's'
              }}</span
            >
            <span class="footer-caption"
              ><i class="fas fa-users" aria-hidden="true"></i> Administrative accounts</span
            >
          </div>
        </div>

        <div v-else class="empty-state">
          <h3>No admin users available</h3>
          <p>There are no admin records to display right now.</p>
        </div>
      </template>
    </section>
    <b-modal
      v-model="adminModalOpen"
      :title="editingAdmin ? 'Edit administrator' : 'Add new administrator'"
      :aria-label="editingAdmin ? 'Edit administrator' : 'Add new administrator'"
      hide-header
      hide-footer
      centered
      size="lg"
      content-class="admin-account-modal"
      :no-close-on-backdrop="saving || editingLoading"
      :no-close-on-esc="saving || editingLoading"
      @hide="onModalHide"
      @hidden="resetAdminForm"
    >
      <div class="admin-modal-hero">
        <span class="admin-modal-icon"><i class="fas fa-user-shield" aria-hidden="true"></i></span>
        <div>
          <span class="hero-kicker">Account management</span>
          <h2>
            {{ editingAdmin ? 'Edit administrator' : 'Add new administrator' }}
          </h2>
          <p>
            {{
              editingAdmin
                ? 'Keep account details and access up to date.'
                : 'Give a team member access to your admin workspace.'
            }}
          </p>
        </div>
        <button
          type="button"
          class="admin-modal-close"
          aria-label="Close admin form"
          :disabled="saving || editingLoading"
          @click="adminModalOpen = false"
        >
          ×
        </button>
      </div>
      <div v-if="editingLoading" class="admin-modal-loading" role="status">
        <b-spinner small /> Loading account details…
      </div>
      <form
        v-else
        class="admin-modal-form"
        novalidate
        :aria-busy="saving"
        @submit.prevent="saveAdmin"
      >
        <p v-if="saveError" class="admin-error" role="alert">{{ saveError }}</p>
        <div class="admin-form-grid">
          <label v-for="field in visibleAdminFields" :key="field.key" class="admin-field">
            <span
              >{{ field.label }}
              <span v-if="field.key !== 'password' || !editingAdmin" class="admin-required"
                >*</span
              ></span
            >
            <input
              :id="`admin-${field.key}`"
              v-model="adminForm[field.key]"
              :type="field.type"
              :placeholder="field.placeholder"
              :autocomplete="field.autocomplete"
              :disabled="saving || detailError"
              :required="field.key !== 'password' || !editingAdmin"
              :aria-invalid="Boolean(fieldErrors[field.key])"
              :aria-describedby="
                fieldErrors[field.key]
                  ? `admin-error-${field.key}`
                  : field.key === 'password'
                  ? 'admin-password-hint'
                  : null
              "
              @input="$delete(fieldErrors, field.key)"
            />
            <small v-if="field.key === 'password'" id="admin-password-hint">{{
              editingAdmin
                ? 'Leave blank to keep the current password.'
                : 'Set a strong password for this account.'
            }}</small>
            <small
              v-if="fieldErrors[field.key]"
              :id="`admin-error-${field.key}`"
              class="admin-field-error"
              >{{ fieldErrors[field.key] }}</small
            >
          </label>
          <div class="admin-field admin-role-field">
            <label for="admin-authorization" class="admin-role-label"
              >Authorization <span class="admin-required">*</span></label
            >
            <multiselect
              id="admin-authorization"
              v-model="adminForm.authorization"
              class="admin-role-select"
              :class="{
                'admin-role-select--invalid': fieldErrors.authorization,
              }"
              :options="availableAuthorizations"
              :disabled="saving || detailError"
              :allow-empty="false"
              :show-labels="false"
              :max-height="280"
              :option-height="58"
              :custom-label="authorizationOptionLabel"
              open-direction="top"
              placeholder="Choose an admin role…"
              aria-required="true"
              :aria-invalid="Boolean(fieldErrors.authorization)"
              :aria-describedby="fieldErrors.authorization ? 'admin-error-authorization' : null"
              @input="$delete(fieldErrors, 'authorization')"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <span class="admin-role-value"
                  ><i class="fas fa-user-shield" aria-hidden="true"></i
                  >{{ authorizationOptionLabel(option) }}</span
                >
              </template>
              <template slot="option" slot-scope="{ option }">
                <span class="admin-role-option">
                  <span class="admin-role-number" aria-hidden="true">{{
                    authorizationOptions.includes(option)
                      ? padIndex(authorizationOptions.indexOf(option))
                      : '•'
                  }}</span>
                  <span class="admin-role-name">{{ authorizationOptionLabel(option) }}</span>
                  <i
                    v-if="adminForm.authorization === option"
                    class="fas fa-check admin-role-check"
                    aria-hidden="true"
                  ></i>
                </span>
              </template>
              <template slot="noResult">No roles match your search.</template>
            </multiselect>
            <small
              v-if="fieldErrors.authorization"
              id="admin-error-authorization"
              class="admin-field-error"
              >{{ fieldErrors.authorization }}</small
            >
          </div>
        </div>
        <div class="admin-modal-footer">
          <span>{{
            editingAdmin
              ? 'Update the account details and authorization.'
              : 'All fields are required.'
          }}</span>
          <div class="admin-modal-actions">
            <button
              type="button"
              class="edit-btn"
              :disabled="saving"
              @click="adminModalOpen = false"
            >
              Cancel
            </button>
            <button
              v-if="detailError"
              type="button"
              class="add-admin-btn"
              @click="setAdminEdit(editingAdmin)"
            >
              Retry loading
            </button>
            <button v-else type="submit" class="add-admin-btn" :disabled="saving">
              <b-spinner v-if="saving" small />{{
                saving ? 'Saving…' : editingAdmin ? 'Save changes' : 'Create admin'
              }}
            </button>
          </div>
        </div>
      </form>
    </b-modal>
    <b-modal
      v-model="deleteModalOpen"
      title="Delete administrator"
      aria-label="Delete administrator"
      dialog-class="admin-confirm-dialog"
      modal-class="admin-confirm-overlay"
      hide-header
      hide-footer
      centered
      content-class="admin-account-modal admin-confirm-modal"
      :no-close-on-backdrop="deleting"
      :no-close-on-esc="deleting"
      @hide="onDeleteModalHide"
    >
      <section class="status-dialog status-dialog--delete" :aria-busy="deleting">
        <div class="status-dialog__header">
          <span class="status-dialog__eyebrow">Account management</span>
          <button
            type="button"
            class="status-dialog__close"
            aria-label="Close delete confirmation"
            :disabled="deleting"
            @click="deleteModalOpen = false"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <div class="status-dialog__body">
          <span class="status-dialog__icon" aria-hidden="true"
            ><i class="far fa-trash-alt"></i
          ></span>
          <span class="status-dialog__step">Remove administrator</span>
          <h2>Delete this administrator?</h2>
          <p class="status-dialog__description">
            This administrator will be removed from the directory. Review the account below before
            continuing.
          </p>
          <div v-if="adminToDelete" class="status-dialog__account">
            <span class="status-dialog__avatar" aria-hidden="true">{{
              getAdminUsername(adminToDelete).slice(0, 2).toUpperCase()
            }}</span>
            <div>
              <strong>{{ getAdminUsername(adminToDelete) }}</strong
              ><span>{{ getAdminEmail(adminToDelete) }}</span>
            </div>
          </div>
          <div class="status-dialog__note">
            <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
            <p>
              This action cannot be undone. To pause access temporarily, disable the account
              instead.
            </p>
          </div>
          <p v-if="deleteError" class="admin-error status-dialog__error" role="alert">
            {{ deleteError }}
          </p>
        </div>
        <div class="status-dialog__footer">
          <button
            type="button"
            class="status-dialog__cancel"
            :disabled="deleting"
            @click="deleteModalOpen = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="status-dialog__confirm"
            :disabled="deleting || !isHeadAdminViewer"
            @click="confirmDeleteAdmin"
          >
            <b-spinner v-if="deleting" small />{{ deleting ? 'Deleting…' : 'Delete admin' }}
          </button>
        </div>
      </section>
    </b-modal>
    <b-modal
      v-model="statusModalOpen"
      dialog-class="admin-confirm-dialog"
      modal-class="admin-confirm-overlay"
      :title="statusWillEnable ? 'Enable administrator' : 'Disable administrator'"
      :aria-label="statusWillEnable ? 'Enable administrator' : 'Disable administrator'"
      hide-header
      hide-footer
      centered
      content-class="admin-account-modal admin-confirm-modal"
      :no-close-on-backdrop="saving"
      :no-close-on-esc="saving"
      @hide="onStatusModalHide"
      @shown="$refs.statusCancelButton && $refs.statusCancelButton.focus()"
      @hidden="resetStatusModal"
    >
      <section
        class="status-dialog status-access-dialog"
        :class="{ 'status-dialog--disable': !statusWillEnable }"
        :aria-busy="saving"
      >
        <div class="status-dialog__header">
          <span class="status-dialog__eyebrow">Account access</span>
          <button
            type="button"
            class="status-dialog__close"
            aria-label="Close confirmation"
            :disabled="saving"
            @click="statusModalOpen = false"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <div class="status-dialog__body">
          <span class="status-dialog__icon" aria-hidden="true">
            <i :class="statusWillEnable ? 'fas fa-unlock-alt' : 'fas fa-user-lock'"></i>
          </span>
          <span class="status-dialog__step">{{
            statusWillEnable ? 'Welcome them back' : 'A reversible change'
          }}</span>
          <h2>{{ statusWillEnable ? 'Restore account access?' : 'Pause account access?' }}</h2>
          <p class="status-dialog__description">
            {{
              statusWillEnable
                ? 'Enable this administrator to let them sign in and resume their work.'
                : 'Disable this administrator to prevent them from signing in to the platform.'
            }}
          </p>
          <div v-if="adminToToggle" class="status-dialog__account">
            <span class="status-dialog__avatar" aria-hidden="true">{{
              getAdminUsername(adminToToggle).slice(0, 2).toUpperCase()
            }}</span>
            <div>
              <strong>{{ getAdminUsername(adminToToggle) }}</strong>
              <span>{{ getAdminEmail(adminToToggle) }}</span>
            </div>
            <span class="status-dialog__badge">{{
              statusWillEnable ? 'Disabled' : 'Enabled'
            }}</span>
          </div>
          <div class="status-dialog__note">
            <i class="fas fa-info-circle" aria-hidden="true"></i>
            <p>
              {{
                statusWillEnable
                  ? 'Their existing role and permissions will stay the same.'
                  : 'The account and its details will be kept. You can enable it again at any time.'
              }}
            </p>
          </div>
          <p v-if="statusError" class="admin-error status-dialog__error" role="alert">
            {{ statusError }}
          </p>
        </div>
        <div class="status-dialog__footer">
          <button
            ref="statusCancelButton"
            type="button"
            class="status-dialog__cancel"
            :disabled="saving"
            @click="statusModalOpen = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="status-dialog__confirm"
            :disabled="saving || !isHeadAdminViewer"
            @click="confirmAdminStatus"
          >
            <b-spinner v-if="saving" small />
            <i v-else class="fas fa-power-off" aria-hidden="true"></i>
            {{
              saving
                ? statusWillEnable
                  ? 'Enabling…'
                  : 'Disabling…'
                : statusWillEnable
                ? 'Enable account'
                : 'Disable account'
            }}
          </button>
        </div>
      </section>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

export default {
  components: { Multiselect },
  data() {
    return {
      loading: true,
      deleteModalOpen: false,
      adminToDelete: null,
      deleting: false,
      deleteError: '',
      loadError: '',
      notice: '',
      statusError: '',
      statusUpdatingId: null,
      statusModalOpen: false,
      adminToToggle: null,
      statusWillEnable: false,
      adminModalOpen: false,
      editingAdmin: null,
      editingLoading: false,
      detailError: false,
      saving: false,
      saveError: '',
      fieldErrors: {},
      adminForm: {
        username: '',
        email: '',
        password: '',
        organization: '',
        position: '',
        authorization: '',
      },
      currentAuthorizationLabel: '',
      authorizationOptions: [
        'Head Super Admin',
        'Super Admin',
        'International Cooperation Admin',
        'Monitoring and Evaluation Admin',
        'Non-Governmental Organization Admin',
        'Organization Admin',
        'Data Admin',
      ],
      adminFields: [
        {
          key: 'username',
          label: 'Username',
          type: 'text',
          placeholder: 'support-admin',
          autocomplete: 'off',
        },
        {
          key: 'email',
          label: 'Email address',
          type: 'email',
          placeholder: 'admin@example.com',
          autocomplete: 'off',
        },
        {
          key: 'organization',
          label: 'Organization',
          type: 'text',
          placeholder: 'eHealth4everyone',
          autocomplete: 'off',
        },
        {
          key: 'position',
          label: 'Position',
          type: 'text',
          placeholder: 'Support Lead',
          autocomplete: 'off',
        },
        {
          key: 'password',
          label: 'Password',
          type: 'password',
          placeholder: 'Enter a password',
          autocomplete: 'new-password',
        },
      ],
    };
  },

  computed: {
    visibleAdminFields() {
      return this.editingAdmin
        ? this.adminFields.filter((field) => field.key !== 'password')
        : this.adminFields;
    },
    ...mapGetters('admin', ['getAllAdmins', 'getAdminById']),

    admins() {
      return this.getAllAdmins?.data?.results || [];
    },
    currentUser() {
      return this.$store?.getters?.['auth/user'] || {};
    },
    isHeadAdminViewer() {
      return this.isHeadAdmin(this.currentUser);
    },
    hasCustomAuthorization() {
      return (
        this.adminForm.authorization &&
        !this.authorizationOptions.includes(this.adminForm.authorization)
      );
    },
    availableAuthorizations() {
      return this.hasCustomAuthorization
        ? [this.adminForm.authorization, ...this.authorizationOptions]
        : this.authorizationOptions;
    },
  },

  methods: {
    ...mapActions('admin', [
      'GET_ADMINS',
      'GET_ADMIN_SINGLE',
      'NEWADMIN',
      'UPDATE_ADMIN',
      'DISABLE_ADMIN',
      'ENABLE_ADMIN',
      'DELETE_ADMIN',
    ]),

    isAdminEnabled(admin) {
      return (admin.user?.is_active ?? admin.is_active) !== false;
    },

    toggleAdminStatus(admin) {
      if (
        !this.isHeadAdminViewer ||
        !admin?.id ||
        this.saving ||
        this.editingLoading ||
        this.deleting ||
        this.adminModalOpen ||
        this.deleteModalOpen ||
        this.statusModalOpen
      )
        return;

      this.adminToToggle = admin;
      this.statusWillEnable = !this.isAdminEnabled(admin);
      this.statusError = '';
      this.statusModalOpen = true;
    },

    onStatusModalHide(event) {
      if (this.saving) event.preventDefault();
    },

    resetStatusModal() {
      if (this.statusModalOpen || this.saving) return;
      this.adminToToggle = null;
      this.statusError = '';
    },

    async confirmAdminStatus() {
      if (
        !this.isHeadAdminViewer ||
        !this.statusModalOpen ||
        !this.adminToToggle?.id ||
        this.saving ||
        this.deleting ||
        this.editingLoading
      )
        return;
      const admin = this.adminToToggle;
      const isActive = this.statusWillEnable;
      this.saving = true;
      this.statusUpdatingId = admin.id;
      this.statusError = '';
      this.notice = '';
      try {
        if (isActive) await this.ENABLE_ADMIN(admin.id);
        else await this.DISABLE_ADMIN(admin.id);
        await this.loadAdmins();
        this.notice = `Administrator ${isActive ? 'enabled' : 'disabled'} successfully.`;
        this.statusModalOpen = false;
      } catch (error) {
        this.statusError =
          error.response?.data?.detail ||
          `Unable to ${isActive ? 'enable' : 'disable'} this administrator. Please try again.`;
      } finally {
        this.saving = false;
        this.statusUpdatingId = null;
      }
    },

    openDeleteAdmin(admin) {
      if (
        !this.isHeadAdminViewer ||
        !admin?.id ||
        this.saving ||
        this.editingLoading ||
        this.deleting ||
        this.statusModalOpen
      )
        return;
      this.adminToDelete = admin;
      this.deleteError = '';
      this.deleteModalOpen = true;
    },
    onDeleteModalHide(event) {
      if (this.deleting) event.preventDefault();
    },
    async confirmDeleteAdmin() {
      if (!this.isHeadAdminViewer || !this.adminToDelete?.id || this.deleting) return;
      this.deleting = true;
      this.deleteError = '';
      try {
        await this.DELETE_ADMIN(this.adminToDelete.id);
        await this.loadAdmins();
        this.deleting = false;
        this.deleteModalOpen = false;
        this.adminToDelete = null;
        this.notice = 'Administrator deleted successfully.';
      } catch (error) {
        this.deleteError =
          error.response?.data?.detail || 'Unable to delete this administrator. Please try again.';
      } finally {
        this.deleting = false;
      }
    },

    authorizationOptionLabel(role) {
      return this.hasCustomAuthorization && role === this.adminForm.authorization
        ? this.currentAuthorizationLabel || role
        : role;
    },

    resetAdminForm() {
      this.adminForm = {
        username: '',
        email: '',
        password: '',
        organization: '',
        position: '',
        authorization: '',
      };
      this.fieldErrors = {};
      this.saveError = '';
      this.detailError = false;
      this.currentAuthorizationLabel = '';
    },
    openAdminModal(admin = null) {
      if (
        !this.isHeadAdminViewer ||
        this.saving ||
        this.editingLoading ||
        this.deleting ||
        this.statusModalOpen
      )
        return;
      this.resetAdminForm();
      this.editingAdmin = admin;
      if (admin) {
        this.adminForm = {
          username: admin.user?.username || admin.username || '',
          email: admin.user?.email || admin.email || '',
          password: '',
          organization: admin.organization || '',
          position: admin.position || '',
          authorization:
            admin.authorization?.value || admin.authorization?.display || admin.authorization || '',
        };
        this.currentAuthorizationLabel = this.getAuthorizationLabel(admin);
      }
      this.adminModalOpen = true;
    },
    onModalHide(event) {
      if (this.saving || this.editingLoading) event.preventDefault();
    },
    async loadAdmins() {
      this.loading = true;
      this.loadError = '';
      try {
        await this.GET_ADMINS();
      } catch (error) {
        this.loadError = 'Unable to load administrators. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    validateAdminForm() {
      const errors = {};
      [...this.adminFields, { key: 'authorization', label: 'Authorization' }].forEach(
        ({ key, label }) => {
          if (key === 'password' && this.editingAdmin) return;
          if (!this.adminForm[key].trim()) errors[key] = `${label} is required.`;
        }
      );
      if (this.adminForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.adminForm.email.trim()))
        errors.email = 'Enter a valid email address.';
      this.fieldErrors = errors;
      if (Object.keys(errors).length) {
        this.$nextTick(() => document.getElementById(`admin-${Object.keys(errors)[0]}`)?.focus());
        return false;
      }
      return true;
    },
    async saveAdmin() {
      if (
        !this.isHeadAdminViewer ||
        this.deleting ||
        this.saving ||
        this.editingLoading ||
        this.detailError ||
        (this.editingAdmin && !this.canEditAdmin(this.editingAdmin)) ||
        !this.validateAdminForm()
      )
        return;
      this.saving = true;
      this.saveError = '';
      const payload = {};
      Object.keys(this.adminForm).forEach((key) => {
        payload[key] = key === 'password' ? this.adminForm[key] : this.adminForm[key].trim();
      });
      if (this.editingAdmin) delete payload.password;
      try {
        if (this.editingAdmin) await this.UPDATE_ADMIN({ id: this.editingAdmin.id, data: payload });
        else await this.NEWADMIN(payload);
        await this.loadAdmins();
        this.notice = this.editingAdmin
          ? 'Administrator updated successfully.'
          : 'Administrator created successfully.';
        this.saving = false;
        this.adminModalOpen = false;
        this.adminForm.password = '';
      } catch (error) {
        const data = error.response?.data;
        const errors = data?.errors || data;
        Object.keys(this.adminForm).forEach((key) => {
          const message = errors?.[key] || errors?.user?.[key] || errors?.user1?.[key];
          if (message)
            this.$set(
              this.fieldErrors,
              key,
              Array.isArray(message) ? message.join(' ') : String(message)
            );
        });
        this.saveError =
          data?.detail ||
          data?.message ||
          'Unable to save this administrator. Please check the details and try again.';
      } finally {
        this.saving = false;
      }
    },

    getAdminUsername(item) {
      return item?.user?.username || item?.username || 'Username unavailable';
    },

    getAdminEmail(item) {
      return item?.user?.email || item?.email || 'Email unavailable';
    },

    getAuthorizationLabel(item) {
      const authorization = item?.authorization;
      if (typeof authorization === 'string') {
        return authorization || 'Not set';
      }

      return authorization?.display || authorization?.value || 'Not set';
    },

    isHeadAdmin(admin = {}) {
      const userType = Number(admin.user?.type ?? admin.type);

      return userType === 6;
    },

    canEditAdmin() {
      return this.isHeadAdminViewer;
    },

    async setAdminEdit(admin) {
      if (
        !admin?.id ||
        !this.canEditAdmin(admin) ||
        this.saving ||
        this.editingLoading ||
        this.deleting ||
        this.statusModalOpen
      ) {
        return;
      }

      this.openAdminModal(admin);
      this.editingLoading = true;
      try {
        const response = await this.GET_ADMIN_SINGLE(admin.id);
        const details = response?.data || this.getAdminById?.data;
        if (!details?.id || !this.canEditAdmin(details)) throw new Error('Unable to edit account');
        this.editingLoading = false;
        this.openAdminModal(details);
      } catch (error) {
        this.detailError = true;
        this.saveError = 'Unable to load this administrator for editing. Please try again.';
      } finally {
        this.editingLoading = false;
      }
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },
  },

  mounted() {
    this.loadAdmins();
  },
};
</script>

<style scoped>
.user-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  color: #243e30;
  background: radial-gradient(ellipse at top right, #e7efe7, transparent 55%), #f6f8f5;
}
.user-shell {
  max-width: 1320px;
  margin: 0 auto;
}
.user-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
  border: 1px solid #e0e8de;
  border-radius: 22px;
  background: linear-gradient(120deg, #fff, #f4f8f2);
  box-shadow: 0 8px 32px #213a2a06;
}
.user-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1.6rem;
  color: #819083;
  font-size: 0.78rem;
}
.breadcrumb-current {
  color: #45644e;
  font-weight: 600;
}
.breadcrumb-separator {
  color: #b0bbb0;
}
.hero-kicker,
.results-kicker {
  margin-bottom: 0.55rem;
  color: #658363;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.hero-copy h1 {
  margin: 0;
  color: #1b3826;
  font-size: clamp(1.9rem, 3.5vw, 2.7rem);
  letter-spacing: -0.045em;
  font-weight: 700;
}
.hero-description {
  max-width: 55ch;
  margin: 0.85rem 0 0;
  font-size: 0.92rem;
  line-height: 1.75;
  color: #768275;
}
.hero-metrics {
  flex-shrink: 0;
}
.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 210px;
  padding: 1.4rem 1.6rem;
  border-left: 1px solid #dfe8dc;
}
.metric-label {
  color: #68806b;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.metric-value {
  color: #2d5537;
  font-size: 2.6rem;
  line-height: 1.2;
  letter-spacing: -0.05em;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.metric-meta {
  color: #899689;
  font-size: 0.76rem;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1.75rem;
  padding: 1.2rem 0;
}
.toolbar-copy h2 {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.directory-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 27px;
  padding: 0.25rem 0.45rem;
  border: 1px solid #dbe6d6;
  border-radius: 7px;
  background: #eaf1e6;
  color: #547746;
  font-size: 0.7rem;
}
.add-admin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem;
  border: 1px solid #255039;
  border-radius: 10px;
  background: linear-gradient(135deg, #234931, #386146);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 6px 14px #20463016;
  text-decoration: none;
}
.add-admin-btn:hover {
  color: #fff;
  background: #21452e;
}
.directory-panel {
  border: 1px solid #e0e7dd;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 12px 40px #233f2b06;
}
.directory-scroll {
  overflow-x: auto;
}
.directory-table {
  width: 100%;
  min-width: 1120px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}
.directory-table thead th {
  padding: 1.1rem 1.2rem;
  border-bottom: 1px solid #e2e9df;
  background: #f6f8f4;
  color: #7c8b7b;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.directory-table tbody th,
.directory-table tbody td {
  padding: 1.35rem 1.2rem;
  border-bottom: 1px solid #edf1e9;
  vertical-align: middle;
  overflow-wrap: anywhere;
  transition: background 0.15s ease;
}
.directory-table tbody tr:last-child > * {
  border-bottom: 0;
}
.directory-table tbody tr:hover > * {
  background: #f8faf6;
}
.directory-table .number-column {
  width: 62px;
}
.directory-table .account-column {
  width: 26%;
}
.directory-table .action-column {
  width: 300px;
  text-align: right;
}
.admin-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
}
.admin-row-actions .edit-btn {
  height: 34px;
  margin: 0;
  padding: 0 0.65rem;
  gap: 0.4rem;
  border-radius: 6px;
  line-height: 1;
}
.admin-row-actions .edit-btn i {
  width: 12px;
  text-align: center;
}
.admin-row-actions .edit-btn:focus-visible {
  outline: 2px solid #66804f;
  outline-offset: 3px;
}
.admin-row-actions .admin-status-btn--disable:not(:disabled) {
  color: #96621c;
  border-color: #eadcc3;
  background: #fffbf3;
}
.admin-row-actions .admin-status-btn--disable:not(:disabled):hover {
  border-color: #cba665;
  background: #fff3da;
}
.admin-row-actions .admin-delete-btn:not(:disabled) {
  background: #fff7f7;
}
.admin-row-actions .admin-delete-btn:not(:disabled):hover {
  background: #ffeded;
}
.admin-row-actions .edit-btn:disabled:hover {
  border-color: #e5ded0;
  background: #f5f2ec;
}
.row-number {
  font-size: 0.73rem;
  color: #98a292;
  font-variant-numeric: tabular-nums;
}
.account-identity {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.account-avatar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid #dfe9d7;
  border-radius: 12px;
  background: #eff5e9;
  color: #66804f;
  font-size: 0.75rem;
  font-weight: 700;
}
.directory-table tbody tr:nth-child(3n + 2) .account-avatar {
  background: #edf2f7;
  border-color: #dfe8f0;
  color: #627e98;
}
.directory-table tbody tr:nth-child(3n) .account-avatar {
  background: #f4f0f7;
  border-color: #e8e0ed;
  color: #8b719b;
}
.account-copy {
  min-width: 0;
}
.account-name {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: #2c4834;
}
.account-email {
  display: block;
  font-size: 0.75rem;
  font-weight: 400;
  color: #859080;
  line-height: 1.6;
}
.organization-name {
  color: #455d4a;
  font-size: 0.83rem;
  font-weight: 500;
  line-height: 1.6;
}
.position-label {
  font-size: 0.8rem;
  color: #7c8877;
  line-height: 1.6;
}
.authorization-badge {
  display: inline-block;
  padding: 0.4rem 0.6rem;
  border: 1px solid #e1e7dd;
  border-radius: 6px;
  background: #f4f7f0;
  color: #587349;
  font-size: 0.69rem;
  font-weight: 600;
  line-height: 1.5;
}
.edit-btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  width: auto;
  padding: 0.6rem 0.8rem;
  border: 1px solid #dce6d6;
  border-radius: 8px;
  background: #fff;
  color: #4b6f3c;
  font-size: 0.74rem;
  font-weight: 600;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.edit-btn:disabled,
.edit-btn--locked {
  cursor: not-allowed;
  border-color: #e5ded0;
  background: #f5f2ec;
  color: #8b806d;
  box-shadow: none;
}
.edit-btn i {
  font-size: 0.65rem;
}
.edit-btn:hover {
  background: #eff5e9;
  border-color: #a6bd95;
}
.directory-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e8ede3;
  background: #fcfdfa;
  color: #8a9584;
  font-size: 0.73rem;
}
.directory-footer strong {
  color: #506a45;
  font-weight: 600;
}
.footer-caption {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 280px;
  padding: 2rem;
  background: #fff;
  border: 1px solid #e0e7dd;
  border-radius: 18px;
  text-align: center;
  color: #75836d;
}
.empty-state h3 {
  font-size: 1.2rem;
  color: #34512e;
}
.empty-state p,
.loading-state p {
  margin: 0;
}
.edit-btn:focus-visible,
.add-admin-btn:focus-visible {
  outline: 3px solid #9db689;
  outline-offset: 3px;
}
.directory-scroll:focus-visible {
  outline: 3px solid #9db689;
  outline-offset: -3px;
}
@media (max-width: 991px) {
  .user-page {
    padding-top: 0;
  }
  .user-hero {
    gap: 1rem;
  }
}
@media (max-width: 767px) {
  .user-page {
    padding: 0;
  }
  .user-hero {
    flex-direction: column;
    align-items: stretch;
    padding: 1.4rem;
  }
  .metric-card {
    border-left: 0;
    border-top: 1px solid #dfe8dc;
    padding: 1rem 0 0;
    min-width: 0;
  }
  .metric-value {
    font-size: 2rem;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .directory-footer {
    flex-wrap: wrap;
  }
}
</style>

<!-- BootstrapVue renders this dialog outside the page. -->
<style>
@import '~vue-multiselect/dist/vue-multiselect.min.css';

.admin-account-modal {
  border: 1px solid #dce8de;
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 32px 100px #17342540;
}
.admin-account-modal .modal-body {
  padding: 0;
}
.modal.admin-confirm-overlay ~ .modal-backdrop {
  background-color: #233b30;
  opacity: 0.45;
}
.modal.admin-confirm-overlay ~ .modal-backdrop.fade:not(.show) {
  opacity: 0;
}
.admin-confirm-dialog {
  max-width: 520px;
}
.admin-confirm-modal {
  border: 1px solid #ffffff;
  border-radius: 24px;
  box-shadow: 0 24px 70px #1e382226, 0 4px 16px #1e382210;
}
@media (max-width: 575.98px) {
  .admin-confirm-dialog {
    margin: 1rem;
  }
}
</style>
<style scoped>
.admin-modal-hero {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 2rem 3.4rem 2rem 2rem;
  background: radial-gradient(circle at top right, #dfeee1, transparent 65%), #f5faf5;
  border-bottom: 1px solid #e0e8de;
}
.admin-delete-btn {
  color: #b43b3b;
  border-color: #f0d4d4;
}
.status-dialog {
  --status-accent: #286747;
  --status-soft: #edf7ef;
  --status-border: #d9e9dd;
  color: #233c30;
  background: radial-gradient(ellipse at 0 0, var(--status-soft), transparent 60%), #fff;
}
.status-dialog--disable {
  --status-accent: #936126;
  --status-soft: #fff7e9;
  --status-border: #efe0c7;
}
.status-dialog--delete {
  --status-accent: #a84343;
  --status-soft: #fff1f0;
  --status-border: #f0d8d6;
}
.status-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.75rem 0;
  background: transparent;
}
.status-dialog__eyebrow {
  color: var(--status-accent);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.status-dialog__close {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid #e5e9e2;
  border-radius: 50%;
  color: #7d877c;
  background: #ffffffd9;
  font-size: 0.75rem;
}
.status-dialog__body {
  padding: 1.25rem 1.75rem 1.75rem;
}
.status-dialog__icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin-bottom: 1.5rem;
  border: 1px solid var(--status-border);
  border-radius: 20px;
  background: linear-gradient(145deg, #fff, var(--status-soft));
  color: var(--status-accent);
  box-shadow: 0 5px 12px #263c3008, inset 0 1px 0 #fff;
  font-size: 1.3rem;
}
.status-dialog__step {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--status-accent);
  font-size: 0.72rem;
  font-weight: 600;
}
.status-dialog h2 {
  margin: 0 0 0.7rem;
  color: #233c30;
  font-size: clamp(1.5rem, 4vw, 1.9rem);
  font-weight: 700;
  letter-spacing: -0.035em;
}
.status-dialog__description {
  margin: 0;
  color: #677464;
  font-size: 0.86rem;
  line-height: 1.75;
}
.status-dialog__account {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.5rem 0 1rem;
  padding: 1.1rem;
  border: 1px solid #e6ebe3;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 3px 10px #263c3005;
}
.status-dialog__avatar {
  display: grid;
  place-items: center;
  flex: 0 0 38px;
  height: 38px;
  border: 1px solid #dfe7da;
  border-radius: 11px;
  background: #eef3e9;
  color: #617a51;
  font-size: 0.75rem;
  font-weight: 700;
}
.status-dialog__account > div {
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
}
.status-dialog__account strong {
  display: block;
  font-size: 0.84rem;
  font-weight: 600;
}
.status-dialog__account > div > span {
  display: block;
  margin-top: 0.2rem;
  color: #7d8879;
  font-size: 0.72rem;
}
.status-dialog__badge {
  padding: 0.3rem 0.5rem;
  border: 1px solid #e3e8df;
  border-radius: 6px;
  color: #75816d;
  font-size: 0.65rem;
}
.status-dialog__note {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  color: #677464;
  padding: 0.8rem 0.9rem;
  border: 1px solid var(--status-border);
  border-radius: 10px;
  background: var(--status-soft);
}
.status-dialog__note i {
  margin-top: 0.25rem;
  color: var(--status-accent);
  font-size: 0.75rem;
}
.status-dialog__note p {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.7;
}
.status-dialog__error {
  margin: 1rem 0 0;
}
.status-dialog__footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
  padding: 1.2rem 1.75rem;
  border-top: 1px solid #eaf0e6;
  background: #ffffffb3;
}
.status-dialog__cancel,
.status-dialog__confirm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  padding: 0.65rem 1rem;
  border: 1px solid #dfe7d9;
  border-radius: 10px;
  background: #fff;
  color: #64735c;
  font-size: 0.8rem;
  font-weight: 600;
  transition: box-shadow 0.15s ease, filter 0.15s ease;
}
.status-dialog__confirm {
  border-color: var(--status-accent);
  background: var(--status-accent);
  color: #fff;
  box-shadow: 0 4px 10px #233c3020, inset 0 1px 0 #ffffff26;
}
.status-dialog button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.status-dialog button:not(:disabled):hover {
  filter: brightness(0.96);
}
.status-dialog button:focus-visible {
  outline: 2px solid var(--status-accent);
  outline-offset: 3px;
}
.account-status {
  color: #3e6b45;
  font-size: 0.74rem;
  font-weight: 600;
}
.account-status--disabled {
  color: #a03a3a;
}
.admin-delete-btn:hover {
  color: #952d2d;
  background: #fff2f2;
  border-color: #e6baba;
}
.admin-delete-dialog {
  padding: 2rem;
}
.admin-delete-dialog h2 {
  margin: 1rem 0 0.7rem;
  font-size: 1.6rem;
  color: #1b3826;
  letter-spacing: -0.04em;
}
.admin-delete-dialog p {
  color: #768275;
  line-height: 1.7;
}
.admin-delete-dialog .admin-modal-actions {
  margin-top: 1.5rem;
  justify-content: flex-end;
}
.admin-delete-icon {
  background: #fff2f2;
  color: #b43b3b;
  border-color: #f0d4d4;
}
.admin-delete-confirm {
  background: #b43b3b;
  border-color: #b43b3b;
}
.admin-delete-confirm:hover {
  background: #952d2d;
}
.admin-modal-hero h2 {
  margin: 0.5rem 0;
  color: #1b3826;
  font-size: 1.8rem;
  letter-spacing: -0.04em;
}
.admin-modal-hero p {
  margin: 0;
  color: #768275;
  line-height: 1.6;
}
.admin-modal-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border: 1px solid #dce8de;
  border-radius: 20px;
  color: #2e643e;
  background: #fff;
  font-size: 1.5rem;
  box-shadow: 0 8px 24px #2349310d;
}
.admin-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 34px;
  height: 34px;
  border: 1px solid #dce8de;
  border-radius: 50%;
  background: #fff;
  color: #45644e;
  font-size: 1.5rem;
  line-height: 1;
}
.admin-modal-form,
.admin-modal-loading {
  padding: 2rem;
}
.admin-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.3rem;
}
.admin-field {
  display: grid;
  align-content: start;
  gap: 0.55rem;
  margin: 0;
  color: #29412f;
  font-size: 0.86rem;
  font-weight: 600;
}
.admin-field input,
.admin-field select {
  width: 100%;
  min-width: 0;
  padding: 0.9rem 1rem;
  border: 1px solid #d7e3db;
  border-radius: 12px;
  background: #fbfdfb;
  color: #243e30;
  font-weight: 400;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.admin-field input:focus,
.admin-field select:focus {
  border-color: #358d50;
  box-shadow: 0 0 0 4px #358d5014;
  background: #fff;
}
.admin-field [aria-invalid='true'] {
  border-color: #c65c5c;
}
.admin-field small {
  color: #7c8877;
  font-weight: 400;
}
.admin-field .admin-field-error {
  color: #b43b3b;
}
.admin-required {
  color: #358d50;
}
.admin-role-field {
  grid-column: 1 / -1;
}
.admin-role-label {
  margin: 0;
}
.admin-role-select {
  font-size: 0.9rem;
  font-weight: 400;
  color: #243e30;
}
.admin-role-select ::v-deep .multiselect__tags {
  min-height: 54px;
  padding: 15px 44px 12px 16px;
  border: 1px solid #d7e3db;
  border-radius: 14px;
  background: linear-gradient(180deg, #fff, #f7faf7);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.admin-role-select.multiselect--active ::v-deep .multiselect__tags {
  border-color: #358d50;
  border-radius: 14px;
  box-shadow: 0 0 0 4px #358d5014;
}
.admin-role-select--invalid ::v-deep .multiselect__tags {
  border-color: #c65c5c;
}
.admin-role-select ::v-deep .multiselect__select {
  height: 54px;
  width: 42px;
}
.admin-role-select ::v-deep .multiselect__select::before {
  border-top-color: #54785e;
}
.admin-role-select ::v-deep .multiselect__single,
.admin-role-select ::v-deep .multiselect__input {
  background: transparent;
  color: #29412f;
  padding: 0;
  margin: 0;
  min-height: 24px;
  font-size: 0.9rem;
  line-height: 24px;
  white-space: normal;
}
.admin-role-select ::v-deep .multiselect__input {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  width: 100%;
}
.admin-role-select ::v-deep .multiselect__placeholder {
  margin: 0;
  padding: 0;
  line-height: 24px;
  color: #829187;
}
.admin-role-select ::v-deep .multiselect__content-wrapper {
  margin-bottom: 10px;
  padding: 6px;
  border: 1px solid #dce8de;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 14px 44px #17342526, 0 2px 8px #1734250d;
  scrollbar-width: thin;
  scrollbar-color: #c9dace transparent;
}
.admin-role-select ::v-deep .multiselect__option {
  padding: 0;
  min-height: 58px;
  border-radius: 10px;
  white-space: normal;
  color: #35503e;
  background: #fff;
}
.admin-role-select ::v-deep .multiselect__option--highlight {
  background: #f0f6f1;
  color: #216f3c;
}
.admin-role-select ::v-deep .multiselect__option--selected {
  background: #e8f3eb;
  color: #216f3c;
  font-weight: 600;
}
.admin-role-value {
  display: flex;
  gap: 0.65rem;
  align-items: center;
  font-weight: 600;
}
.admin-role-value i {
  color: #358d50;
  font-size: 0.9rem;
}
.admin-role-option {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 12px;
  min-height: 58px;
}
.admin-role-number {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid #dfe9e1;
  border-radius: 9px;
  background: #f6faf7;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64826c;
}
.admin-role-name {
  flex: 1;
  line-height: 1.45;
}
.admin-role-check {
  font-size: 0.8rem;
  color: #358d50;
}
.admin-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.4rem;
  border-top: 1px solid #e0e8de;
}
.admin-modal-footer > span {
  font-size: 0.8rem;
  color: #7c8877;
}
.admin-modal-actions {
  display: flex;
  gap: 0.7rem;
}
.admin-notice,
.admin-error {
  padding: 1rem 1.2rem;
  margin: 0 0 1.3rem;
  border-radius: 12px;
  line-height: 1.6;
}
.admin-notice {
  border: 1px solid #cbe3d1;
  background: #edf7ef;
  color: #216f3c;
}
.admin-error {
  border: 1px solid #f0d4d4;
  background: #fff2f2;
  color: #a33232;
}
.add-admin-btn:disabled,
.admin-modal-close:disabled,
.admin-field input:disabled,
.admin-field select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.admin-modal-close:focus-visible {
  outline: 3px solid #9db689;
  outline-offset: 3px;
}
@media (max-width: 640px) {
  .admin-modal-hero {
    padding: 1.5rem 3rem 1.5rem 1.2rem;
  }
  .admin-modal-icon {
    display: none;
  }
  .admin-modal-hero h2 {
    font-size: 1.5rem;
  }
  .admin-modal-form,
  .admin-modal-loading {
    padding: 1.2rem;
  }
  .admin-form-grid {
    grid-template-columns: 1fr;
  }
  .admin-modal-actions {
    width: 100%;
  }
  .admin-modal-actions button {
    flex: 1;
  }
}
</style>
